import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { type AppLang } from '../i18n/fallback-translations';
import { getBundledTranslations } from '../loaders/translate-fallback.loader';

export type { AppLang };

const STORAGE_KEY = 'app_lang';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly supported: AppLang[] = ['pt', 'en'];

  constructor(private translate: TranslateService) {}

  /** Garante que pt e en estão no store desde o arranque (browser em pt às vezes não dispara o loader para pt). */
  private ensureBundledInStore(): void {
    this.translate.setTranslation('pt', getBundledTranslations('pt') as Record<string, unknown>, true);
    this.translate.setTranslation('en', getBundledTranslations('en') as Record<string, unknown>, true);
  }

  init(): void {
    const local = this.getLocalPreference();
    const resolved = this.normalize(local) ?? this.getBrowserLanguage() ?? 'pt';

    this.translate.setDefaultLang('pt');
    this.translate.use(resolved);
  }

  /** Inicializa o idioma e retorna uma Promise que resolve quando as traduções estiverem carregadas (para APP_INITIALIZER). */
  initAsync(): Promise<void> {
    const local = this.getLocalPreference();
    const resolved = this.normalize(local) ?? this.getBrowserLanguage() ?? 'pt';
    this.translate.setDefaultLang('pt');
    this.ensureBundledInStore();
    return firstValueFrom(this.translate.use(resolved))
      .then(() => undefined)
      .catch((err) => {
        console.warn('[LanguageService] Falha ao carregar traduções (verifique se /assets/i18n/*.json está acessível em produção):', err);
        this.applyFallbackTranslations(resolved);
        return undefined;
      });
  }

  /** Aplica traduções de fallback para login/auth quando o carregamento remoto falha (ex.: Azure). */
  private applyFallbackTranslations(lang: AppLang): void {
    this.ensureBundledInStore();
  }

  /**
   * Detecta idioma do navegador de forma robusta.
   * Prioridade: 1) lista de preferências (navigator.languages[0]), 2) navigator.language, 3) userLanguage (IE), 4) html lang.
   */
  getBrowserLanguage(): AppLang | null {
    if (typeof navigator === 'undefined') return null;
    const candidates: string[] = [];
    if (navigator.languages?.length) {
      candidates.push(...navigator.languages);
    }
    if (navigator.language) candidates.push(navigator.language);
    if ((navigator as any).userLanguage) candidates.push((navigator as any).userLanguage);
    if (typeof document !== 'undefined' && document.documentElement?.lang) {
      candidates.push(document.documentElement.lang);
    }
    for (const raw of candidates) {
      const normalized = this.normalize(raw || '');
      if (normalized) return normalized;
    }
    return null;
  }

  change(lang: AppLang): void {
    this.translate.use(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }

  getLocalPreference(): AppLang | null {
    return this.normalize(localStorage.getItem(STORAGE_KEY));
  }

  getCurrent(): AppLang {
    const cur = this.translate.currentLang || this.translate.defaultLang || 'pt';
    return this.normalize(cur) ?? 'pt';
  }

  /** Normaliza qualquer variante (pt-BR, pt_PT, ptb, en-US, etc.) para 'pt' ou 'en'. */
  private normalize(value?: string | null): AppLang | null {
    if (!value || typeof value !== 'string') return null;
    const v = value.toLowerCase().replace('_', '-').trim();
    if (!v) return null;
    const candidate: AppLang | null =
      v.startsWith('pt') ? 'pt' :
      v.startsWith('en') ? 'en' :
      null;
    return candidate && this.supported.includes(candidate) ? candidate : null;
  }
}
